import React, { useState, useEffect, useRef } from 'react';

/**
 * BackfillBanner — shows a banner at the top of the dashboard whenever any
 * of the user's websites is currently doing a 16-month GSC history backfill,
 * OR has just completed/failed one in the last 60 seconds.
 *
 * Polls /backfill-status-all every 5s while any backfill is running.
 * Stops polling once nothing is running.
 */
function BackfillBanner() {
  const [websites, setWebsites] = useState([]);
  const [dismissed, setDismissed] = useState(new Set()); // ids manually dismissed
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const r = await fetch('/keyword-intelligence/api/v1/websites/backfill-status-all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) return;
      const j = await r.json();
      setWebsites(j.websites || []);
    } catch (e) {
      // silent — banner is non-critical
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll every 5 seconds while any are running
    pollRef.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  // Filter to relevant items: running, OR completed/failed in last 60s, AND not dismissed
  const now = Date.now();
  const visible = websites.filter(w => {
    if (dismissed.has(w.id)) return false;
    if (w.status === 'running') return true;
    if ((w.status === 'completed' || w.status === 'failed') && w.completed_at) {
      const age = now - new Date(w.completed_at).getTime();
      return age < 60_000; // show for 60s after completion
    }
    return false;
  });

  if (visible.length === 0) return null;

  const dismiss = (id) => setDismissed(prev => new Set(prev).add(id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 24px 0' }}>
      {visible.map(w => {
        const p = w.progress || {};
        const pct = p.months_total ? Math.round((p.months_done / p.months_total) * 100) : 0;

        if (w.status === 'running') {
          return (
            <div key={w.id} style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 18 }}>⏳</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
                  Pulling history for {w.display_name}
                </div>
                <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
                  {p.months_done || 0} of {p.months_total || 16} months done
                  {(p.rows_inserted > 0 || p.rows_updated > 0) && (
                    <span> · {((p.rows_inserted || 0) + (p.rows_updated || 0)).toLocaleString()} rows imported</span>
                  )}
                  {p.current_chunk && <span style={{ marginLeft: 6, color: '#94a3b8' }}>· processing {p.current_chunk}</span>}
                </div>
              </div>
              <div style={{ width: 180, height: 6, background: '#dbeafe', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: '#3b82f6', transition: 'width 0.5s' }} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', minWidth: 36, textAlign: 'right' }}>
                {pct}%
              </div>
            </div>
          );
        }

        if (w.status === 'completed') {
          const total = (p.rows_inserted || 0) + (p.rows_updated || 0);
          return (
            <div key={w.id} style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
              padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 18 }}>✓</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                  Backfill complete for {w.display_name}
                </div>
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 2 }}>
                  {p.months_total || 16} months of history imported · {total.toLocaleString()} rows
                </div>
              </div>
              <button onClick={() => dismiss(w.id)}
                style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Dismiss
              </button>
            </div>
          );
        }

        if (w.status === 'failed') {
          return (
            <div key={w.id} style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
              padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 18 }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>
                  Backfill failed for {w.display_name}
                </div>
                <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>
                  {w.error || 'Unknown error'} · You can retry from Settings.
                </div>
              </div>
              <button onClick={() => dismiss(w.id)}
                style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Dismiss
              </button>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

export default BackfillBanner;
